import type { GlobalAuditEntry } from '@/mocks/saGlobalAudit';
import { actionLabels, moduleLabels, moduleIcons, severityLabels } from '@/mocks/saGlobalAudit';

interface AuditEntryDetailModalProps {
  entry: GlobalAuditEntry | null;
  onClose: () => void;
}

const resultBadge: Record<string, { bg: string; text: string; icon: string; label: string }> = {
  success: { bg: 'bg-emerald-100', text: 'text-emerald-700', icon: 'ri-check-line', label: 'Éxito' },
  failure: { bg: 'bg-red-100', text: 'text-red-600', icon: 'ri-close-circle-line', label: 'Fallido' },
  pending: { bg: 'bg-sky-100', text: 'text-sky-700', icon: 'ri-time-line', label: 'Pendiente' },
};

const severityBadge: Record<string, { bg: string; text: string; icon: string }> = {
  info: { bg: 'bg-foreground-100', text: 'text-foreground-600', icon: 'ri-information-line' },
  warning: { bg: 'bg-amber-100', text: 'text-amber-700', icon: 'ri-alert-line' },
  critical: { bg: 'bg-red-100', text: 'text-red-600', icon: 'ri-error-warning-line' },
};

export default function AuditEntryDetailModal({ entry, onClose }: AuditEntryDetailModalProps) {
  if (!entry) return null;

  const result = resultBadge[entry.result];
  const severity = severityBadge[entry.severity];

  return (
    <div
      className="fixed inset-0 z-50 flex items-center justify-center p-4"
      onClick={onClose}
    >
      <div className="absolute inset-0 bg-black/40 backdrop-blur-sm"></div>

      <div
        className="relative w-full max-w-xl bg-background-50 rounded-2xl border border-background-200 max-h-[90vh] overflow-y-auto"
        onClick={(e) => e.stopPropagation()}
      >
        {/* Header */}
        <div className="sticky top-0 bg-background-50 border-b border-background-200 px-5 py-4 rounded-t-2xl z-10">
          <div className="flex items-center justify-between">
            <div>
              <h3 className="text-foreground-950 font-bold text-sm">{actionLabels[entry.action]}</h3>
              <p className="text-foreground-400 text-[10px] mt-0.5">Registro #{entry.id} · Session: {entry.sessionId}</p>
            </div>
            <button
              onClick={onClose}
              className="w-8 h-8 rounded-lg bg-background-100 flex items-center justify-center cursor-pointer hover:bg-background-200 transition-colors"
            >
              <i className="ri-close-line text-foreground-500"></i>
            </button>
          </div>

          <div className="flex items-center gap-2 mt-3 flex-wrap">
            <span className={`inline-flex items-center gap-1 px-2.5 py-1 rounded-full text-[10px] font-medium ${result.bg} ${result.text}`}>
              <i className={`${result.icon} text-[9px]`}></i>
              {result.label}
            </span>
            <span className={`inline-flex items-center gap-1 px-2.5 py-1 rounded-full text-[10px] font-medium ${severity.bg} ${severity.text}`}>
              <i className={`${severity.icon} text-[9px]`}></i>
              {severityLabels[entry.severity]}
            </span>
            <span className="inline-flex items-center gap-1 px-2.5 py-1 rounded-full bg-background-100 text-foreground-500 text-[10px] font-medium">
              <i className={`${moduleIcons[entry.module]} text-[9px]`}></i>
              {moduleLabels[entry.module]}
            </span>
          </div>
        </div>

        {/* Body */}
        <div className="p-5 space-y-4">
          {/* User info */}
          <div className="bg-background-100 rounded-xl p-4">
            <p className="text-foreground-400 text-[9px] font-semibold uppercase tracking-wider mb-3">Usuario</p>
            <div className="flex items-center gap-3">
              <div className="w-12 h-12 rounded-xl bg-background-200 flex items-center justify-center shrink-0">
                <i className="ri-user-3-line text-foreground-500 text-xl"></i>
              </div>
              <div>
                <p className="text-foreground-800 text-sm font-semibold">{entry.userName}</p>
                <p className="text-foreground-400 text-[10px]">{entry.userRole} · ID: {entry.userId}</p>
              </div>
            </div>
          </div>

          {/* Action + Target grid */}
          <div className="grid grid-cols-2 gap-3">
            <div className="bg-background-100 rounded-xl p-3">
              <p className="text-foreground-400 text-[9px] font-medium mb-1">Acción</p>
              <p className="text-foreground-800 text-xs font-semibold">{actionLabels[entry.action]}</p>
            </div>
            <div className="bg-background-100 rounded-xl p-3">
              <p className="text-foreground-400 text-[9px] font-medium mb-1">Módulo</p>
              <p className="text-foreground-800 text-xs font-semibold flex items-center gap-1">
                <i className={`${moduleIcons[entry.module]} text-xs`}></i>
                {moduleLabels[entry.module]}
              </p>
            </div>
            <div className="col-span-2 bg-background-100 rounded-xl p-3">
              <p className="text-foreground-400 text-[9px] font-medium mb-1">Objetivo</p>
              <p className="text-foreground-800 text-xs font-semibold">{entry.targetName}</p>
              <p className="text-foreground-400 text-[10px] font-mono mt-0.5">ID: {entry.targetId}</p>
            </div>
          </div>

          {/* Date + IP */}
          <div className="grid grid-cols-2 gap-3">
            <div className="bg-background-100 rounded-xl p-3">
              <p className="text-foreground-400 text-[9px] font-medium mb-1">Fecha y hora</p>
              <p className="text-foreground-800 text-xs font-semibold">{entry.date}</p>
            </div>
            <div className="bg-background-100 rounded-xl p-3">
              <p className="text-foreground-400 text-[9px] font-medium mb-1">Dirección IP</p>
              <p className="text-foreground-800 text-xs font-mono">{entry.ipAddress}</p>
            </div>
          </div>

          {/* Geo + Device */}
          <div className="bg-background-100 rounded-xl p-4">
            <p className="text-foreground-400 text-[9px] font-semibold uppercase tracking-wider mb-3">Ubicación y Dispositivo</p>
            <div className="grid grid-cols-2 gap-3">
              <div>
                <p className="text-foreground-400 text-[9px] font-medium mb-0.5">País</p>
                <p className="text-foreground-800 text-xs font-semibold">{entry.country}</p>
              </div>
              <div>
                <p className="text-foreground-400 text-[9px] font-medium mb-0.5">Ciudad</p>
                <p className="text-foreground-800 text-xs font-semibold">{entry.city}</p>
              </div>
              <div>
                <p className="text-foreground-400 text-[9px] font-medium mb-0.5">Dispositivo</p>
                <p className="text-foreground-800 text-xs font-semibold">{entry.device}</p>
              </div>
              <div>
                <p className="text-foreground-400 text-[9px] font-medium mb-0.5">Navegador</p>
                <p className="text-foreground-800 text-xs font-semibold">{entry.browser}</p>
              </div>
              <div className="col-span-2">
                <p className="text-foreground-400 text-[9px] font-medium mb-0.5">Sistema Operativo</p>
                <p className="text-foreground-800 text-xs font-semibold">{entry.os}</p>
              </div>
            </div>
          </div>

          {/* Details text */}
          <div className="bg-background-100 rounded-xl p-4">
            <p className="text-foreground-400 text-[9px] font-semibold uppercase tracking-wider mb-2">Detalles</p>
            <p className="text-foreground-700 text-xs leading-relaxed">{entry.details}</p>
          </div>
        </div>

        {/* Footer */}
        <div className="sticky bottom-0 bg-background-50 border-t border-background-200 px-5 py-3 rounded-b-2xl flex items-center justify-between">
          <span className="text-foreground-400 text-[10px] font-mono">Session ID: {entry.sessionId}</span>
          <button
            onClick={onClose}
            className="flex items-center gap-1.5 px-4 py-2 rounded-xl bg-foreground-950 text-background-50 text-xs font-bold hover:bg-foreground-800 transition-colors cursor-pointer whitespace-nowrap"
          >
            Cerrar
          </button>
        </div>
      </div>
    </div>
  );
}